import React, { useEffect, useRef } from 'react';
import { decodeRLE } from '../utils/decodeRLE';

const getColorForLabel = (labelId) => {
  const normalizedId = Number.isFinite(Number(labelId)) ? Number(labelId) : 0;
  const hue = (normalizedId * 137.508) % 360;
  return `hsla(${hue}, 85%, 50%, 0.45)`;
};

const normalizeMaskEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;

  if (!entry.rle_gt || !Array.isArray(entry.rle_gt.size) || entry.rle_gt.size.length < 2) {
    return null;
  }

  if (entry.type === 'bbox') {
    return null;
  }

  return {
    rle: entry.rle_gt,
    labelId: entry.label_id ?? 0,
  };
};

const normalizeBboxEntry = (entry) => {
  if (!entry || typeof entry !== 'object' || entry.type !== 'bbox') return null;

  let x;
  let y;
  let width;
  let height;

  if (Array.isArray(entry.bbox) && entry.bbox.length >= 4) {
    [x, y, width, height] = entry.bbox;
  } else if (
    Array.isArray(entry.points) &&
    entry.points.length >= 2 &&
    Array.isArray(entry.points[0]) &&
    Array.isArray(entry.points[1])
  ) {
    const [x1, y1] = entry.points[0];
    const [x2, y2] = entry.points[1];
    x = x1;
    y = y1;
    width = Number(x2) - Number(x1);
    height = Number(y2) - Number(y1);
  } else if (Array.isArray(entry.points) && entry.points.length >= 4) {
    const [x1, y1, x2, y2] = entry.points;
    x = x1;
    y = y1;
    width = Number(x2) - Number(x1);
    height = Number(y2) - Number(y1);
  } else if (
    Number.isFinite(Number(entry.x)) &&
    Number.isFinite(Number(entry.y)) &&
    Number.isFinite(Number(entry.width)) &&
    Number.isFinite(Number(entry.height))
  ) {
    x = entry.x;
    y = entry.y;
    width = entry.width;
    height = entry.height;
  } else {
    return null;
  }

  const sourceSize = Array.isArray(entry.size)
    ? entry.size
    : Array.isArray(entry.image_size)
      ? entry.image_size
      : null;

  return {
    x: Number(x),
    y: Number(y),
    width: Number(width),
    height: Number(height),
    sourceSize,
    labelId: entry.label_id ?? 0,
  };
};

const MaskedImage = ({ imageUrl, rleMasks, type = "mask" }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    const draw = () => {
      const imgWidth = img.width;
      const imgHeight = img.height;

      canvas.width = imgWidth;
      canvas.height = imgHeight;

      // Draw the input image first
      ctx.drawImage(img, 0, 0, imgWidth, imgHeight);

      const overlays = Array.isArray(rleMasks) ? rleMasks : [];

      if (type === 'bbox') {
        overlays.forEach((bboxEntry) => {
          const normalizedBbox = normalizeBboxEntry(bboxEntry);
          if (!normalizedBbox) return;

          const { x, y, width, height, sourceSize, labelId } = normalizedBbox;
          if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
            return;
          }

          const [sourceHeight, sourceWidth] = Array.isArray(sourceSize) && sourceSize.length >= 2
            ? sourceSize
            : [imgHeight, imgWidth];

          const xScale = imgWidth / Number(sourceWidth || imgWidth);
          const yScale = imgHeight / Number(sourceHeight || imgHeight);

          const drawX = x * xScale;
          const drawY = y * yScale;
          const drawWidth = width * xScale;
          const drawHeight = height * yScale;

          ctx.strokeStyle = getColorForLabel(labelId).replace('0.45', '0.95');
          ctx.lineWidth = 10;
          ctx.strokeRect(drawX, drawY, drawWidth, drawHeight);
        });
      } else {
        overlays.forEach((maskEntry) => {
          const normalizedMask = normalizeMaskEntry(maskEntry);
          if (!normalizedMask) return;

          const { rle, labelId } = normalizedMask;

          const mask = decodeRLE(rle);
          const [maskHeight, maskWidth] = rle.size;

          if (!mask.length || maskWidth <= 0 || maskHeight <= 0) return;

          ctx.fillStyle = getColorForLabel(labelId);

          const xScale = imgWidth / maskWidth;
          const yScale = imgHeight / maskHeight;
          const pixelWidth = Math.max(1, Math.ceil(xScale));
          const pixelHeight = Math.max(1, Math.ceil(yScale));

          for (let y = 0; y < maskHeight; y++) {
            for (let x = 0; x < maskWidth; x++) {
              const maskIndex = y * maskWidth + x;
              if (mask[maskIndex]) {
                const canvasX = Math.floor(x * xScale);
                const canvasY = Math.floor(y * yScale);
                ctx.fillRect(canvasX, canvasY, pixelWidth, pixelHeight);
              }
            }
          }
        });
      }

    };

    if (img.complete && img.naturalWidth > 0) {
      draw();
    } else {
      img.onload = draw;
    }

    return () => {
      img.onload = null;
    };
  }, [imageUrl, rleMasks, type]);

  return (
    <div style={{ position: 'relative' }}>
      <img ref={imageRef} src={imageUrl} alt="input" style={{ display: 'none' }} />
      <canvas ref={canvasRef} style={{ maxWidth: '100%', borderRadius: 8 }} />
    </div>
  );
};

export default MaskedImage;